<template>
  <div class="registered-details-page">
    <div class="container message-wrapper py-4 py-lg-5">
      <button class="back-button" @click="$router.push('/ngo/registered')">
        <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
        <span>Back to Registered NGOs</span>
      </button>

      <div v-if="isLoading" class="loading-shell">
        <div class="spinner-border load-icon" role="status"></div>
        <p>Loading organization details...</p>
      </div>

      <div v-else-if="organization !== null" class="details-shell">
        <section class="details-hero">
          <div class="hero-copy">
            <p class="hero-eyebrow">Registered NGO Profile</p>
            <h1>{{ organization.name }}</h1>
            <p class="hero-description">
              Review the registered organization record, inspect supporting documentation, and
              verify current operational status from one refined overview.
            </p>

            <div class="hero-meta">
              <span class="hero-pill hero-pill--primary">
                {{ organization.status || 'Status unavailable' }}
              </span>
              <span class="hero-pill hero-pill--muted">{{ organization.state || 'No state' }}</span>
              <span class="hero-pill hero-pill--muted">{{
                organization.geo_zone || 'No geo zone'
              }}</span>
            </div>
          </div>

          <div class="hero-stats">
            <div class="hero-stat">
              <span class="hero-stat__label">Contacts</span>
              <strong class="hero-stat__value">{{ contact ? contact.length : 0 }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Focus areas</span>
              <strong class="hero-stat__value">{{
                organization.areas_of_focus ? organization.areas_of_focus.length : 0
              }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Active</span>
              <strong class="hero-stat__value">{{ organization.active ? 'Yes' : 'No' }}</strong>
            </div>
          </div>
        </section>

        <section class="section-nav">
          <div class="section-nav__copy">
            <p class="section-kicker">Jump to section</p>
            <h3>Move through the organization profile faster</h3>
          </div>
          <div class="section-nav__chips">
            <a
              v-for="item in sectionLinks"
              :key="item.id"
              class="section-chip"
              :href="`#section-${item.id}`"
            >
              {{ item.label }}
            </a>
          </div>
        </section>

        <div class="details-grid">
          <div class="details-main">
            <section id="section-general" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">General</p>
                <h2>General information</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item">
                  <label>Tracking ID</label>
                  <div>{{ organization.tracking_id || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>File No.</label>
                  <div>{{ organization.file_no || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>General Email</label>
                  <div>{{ organization.email || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Web URL</label>
                  <div>{{ organization.web_url || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>State</label>
                  <div>{{ organization.state || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Geo Zone</label>
                  <div>{{ organization.geo_zone || 'Not Provided' }}</div>
                </div>
              </div>
            </section>

            <section id="section-contact" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Contacts</p>
                <h2>Contact information</h2>
              </div>
              <div v-if="contact && contact.length" class="contact-grid">
                <article
                  v-for="(person, index) in contact"
                  :key="person.id || index"
                  class="contact-card"
                >
                  <div class="contact-card__header">
                    <div class="contact-card__identity">
                      <div class="contact-card__avatar">{{ contactInitials(person) }}</div>
                      <div>
                        <p class="contact-card__eyebrow">Contact Person {{ index + 1 }}</p>
                        <h3>{{ getContactName(person) }}</h3>
                      </div>
                    </div>
                    <span class="contact-card__status">Profile</span>
                  </div>
                  <div class="contact-card__body">
                    <div class="contact-card__item contact-card__item--wide">
                      <span>Full Name</span>
                      <p>{{ getContactName(person) }}</p>
                    </div>
                    <div class="contact-card__item">
                      <span>Email</span>
                      <p>{{ person.email || 'Not Provided' }}</p>
                    </div>
                    <div class="contact-card__item">
                      <span>Phone</span>
                      <p>{{ getContactPhone(person) }}</p>
                    </div>
                    <div class="contact-card__item contact-card__item--wide">
                      <span>Address</span>
                      <p>{{ person.address || 'Not Provided' }}</p>
                    </div>
                  </div>
                </article>
              </div>
              <p v-else class="empty-copy">No Contact Available</p>
            </section>

            <section id="section-basic" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Basic</p>
                <h2>Basic information</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item">
                  <label>Type</label>
                  <div>{{ organization.type || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Establishment Year</label>
                  <div>{{ organization.establishment_year || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Approval Status</label>
                  <div>{{ organization.status || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Subscription Status</label>
                  <div>N/A</div>
                </div>
                <div class="detail-item">
                  <label>Active</label>
                  <div>{{ organization.active ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item">
                  <label>User Email</label>
                  <div>{{ getUserEmail(organization) }}</div>
                </div>
                <div class="detail-item">
                  <label>Contact Position</label>
                  <div>{{ organization.contact_position || 'Not Provided' }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Branch</label>
                  <div
                    v-if="getListValues(organization.branch || organization.branch_states).length"
                    class="tag-wrap"
                  >
                    <span
                      v-for="(branchState, index) in getListValues(
                        organization.branch || organization.branch_states
                      )"
                      :key="`${branchState}-${index}`"
                      class="tags"
                    >
                      {{ branchState }}
                    </span>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Umbrella Organization</label>
                  <div v-if="getListValues(organization.umbrella_org).length" class="tag-wrap">
                    <span
                      v-for="(umbrellaOrganization, index) in getListValues(
                        organization.umbrella_org
                      )"
                      :key="`${umbrellaOrganization}-${index}`"
                      class="tags"
                    >
                      {{ umbrellaOrganization }}
                    </span>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
                <div class="detail-item">
                  <label>Is Umbrella</label>
                  <div>{{ organization.is_umbrella_organization ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item">
                  <label>Collaborative</label>
                  <div>{{ organization.is_collaborative ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Area of Focus</label>
                  <div v-if="organization.areas_of_focus && organization.areas_of_focus.length">
                    <div class="tag-wrap">
                      <span
                        v-for="(focus, index) in organization.areas_of_focus"
                        :key="index"
                        class="tags"
                      >
                        {{ focus.name }}
                      </span>
                    </div>
                  </div>
                  <div v-else>Not Available</div>
                </div>
              </div>
            </section>

            <section id="section-funding" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Funding</p>
                <h2>Banking and funding</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item detail-item--wide detail-item--plain detail-item--no-shell">
                  <label>Bankers</label>
                  <div
                    v-if="organization.bankers && organization.bankers.length"
                    class="plain-surface banker-surface"
                  >
                    <div class="banker-grid">
                      <article
                        v-for="(banker, index) in organization.bankers"
                        :key="banker.id || index"
                        class="banker-card"
                      >
                        <div class="banker-card__body">
                          <div class="banker-card__item">
                            <span>Bank Name</span>
                            <p>{{ banker.name || `Bank ${index + 1}` }}</p>
                          </div>
                          <div class="banker-card__item">
                            <span>Address</span>
                            <p>{{ banker.address || 'Not Provided' }}</p>
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Source of Funding</label>
                  <div>{{ organization.source_of_funding || 'Not Provided' }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Funding List</label>
                  <div>{{ organization.funding_list || 'Not Provided' }}</div>
                </div>
              </div>
            </section>

            <section id="section-extra" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Additional</p>
                <h2>Additional details</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item detail-item--wide detail-item--plain detail-item--no-shell">
                  <label>Past Projects</label>
                  <div
                    v-if="organization.past_projects && organization.past_projects.length"
                    class="plain-surface project-surface"
                  >
                    <div class="project-grid">
                      <article
                        v-for="(project, index) in organization.past_projects"
                        :key="project.id || index"
                        class="project-card"
                      >
                        <div class="project-card__header">
                          <h4>{{ project.title || `Project ${index + 1}` }}</h4>
                        </div>
                        <div class="project-card__body">
                          <div class="project-card__row">
                            <span>Funder</span>
                            <p>{{ project.funder || 'Not Provided' }}</p>
                          </div>
                          <div class="project-card__row">
                            <span>Duration</span>
                            <p>{{ project.duration || 'Not Provided' }}</p>
                          </div>
                          <div class="project-card__row">
                            <span>Amount</span>
                            <p>{{ formatCurrency(project.amount) }}</p>
                          </div>
                          <div class="project-card__row">
                            <span>Created</span>
                            <p>{{ formatDisplayDate(project.created_at) }}</p>
                          </div>
                          <div class="project-card__row">
                            <span>Last Updated</span>
                            <p>{{ formatDisplayDate(project.updated_at) }}</p>
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
                <div class="detail-item">
                  <label>International Affiliation</label>
                  <div>{{ organization.international_affiliation || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Local Affiliation</label>
                  <div>{{ organization.local_affiliation || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Organization Focus</label>
                  <div>{{ organization.organization_focus || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Number of Staffs</label>
                  <div>{{ organization.number_of_staffs || 'Not Provided' }}</div>
                </div>
                <div class="detail-item">
                  <label>Scope of Coverage</label>
                  <div>{{ organization.scope_of_coverage || 'Not Provided' }}</div>
                </div>
                <div class="detail-item detail-item--wide">
                  <label>Other Assets</label>
                  <div>{{ organization.other_assets || 'Not Provided' }}</div>
                </div>
                <div class="detail-item detail-item--wide detail-item--plain detail-item--no-shell">
                  <label>Assets</label>
                  <div
                    v-if="organization.assets && organization.assets.length"
                    class="plain-surface project-surface"
                  >
                    <div class="project-grid">
                      <article
                        v-for="(asset, index) in organization.assets"
                        :key="asset.id || index"
                        class="project-card"
                      >
                        <div class="project-card__header">
                          <h4>{{ asset.name || `Asset ${index + 1}` }}</h4>
                        </div>
                        <div class="project-card__body">
                          <div class="project-card__row">
                            <span>Address</span>
                            <p>{{ asset.address || 'Not Provided' }}</p>
                          </div>
                          <div class="project-card__row">
                            <span>Value</span>
                            <p>{{ formatCurrency(asset.value) }}</p>
                          </div>
                          <div class="project-card__row project-card__row--wide">
                            <span>Description</span>
                            <p>{{ asset.description || 'Not Provided' }}</p>
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>
                  <div v-else>Not Provided</div>
                </div>
              </div>
            </section>

            <section id="section-resources" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Resources</p>
                <h2>Resource persons and physical facilities</h2>
              </div>

              <div class="resource-layout">
                <div class="resource-block">
                  <div class="resource-block__header">
                    <p class="resource-block__eyebrow">People</p>
                    <h3>Resource persons</h3>
                  </div>

                  <div
                    v-if="organization.resource_persons && organization.resource_persons.length"
                    class="resource-grid"
                  >
                    <article
                      v-for="(person, index) in organization.resource_persons"
                      :key="person.id || index"
                      class="resource-card"
                    >
                      <div class="resource-card__header">
                        <h4>{{ person.name || `Resource Person ${index + 1}` }}</h4>
                      </div>

                      <div class="resource-card__body">
                        <div class="resource-card__row">
                          <span>Name</span>
                          <p>{{ person.name || 'Not Provided' }}</p>
                        </div>
                        <div class="resource-card__row">
                          <span>CV</span>
                          <template v-if="person.cv_url">
                            <a :href="person.cv_url" target="_blank">View resource</a>
                          </template>
                          <p v-else>Not Provided</p>
                        </div>
                      </div>
                    </article>
                  </div>

                  <p v-else class="empty-copy">No resource persons available.</p>
                </div>

                <div class="resource-block">
                  <div class="resource-block__header">
                    <p class="resource-block__eyebrow">Assets</p>
                    <h3>Physical facilities</h3>
                  </div>

                  <div
                    v-if="
                      organization.physical_facilities && organization.physical_facilities.length
                    "
                    class="facility-list"
                  >
                    <article
                      v-for="(facility, index) in organization.physical_facilities"
                      :key="facility.id || facility.facility_name || index"
                      class="facility-card"
                    >
                      <div class="facility-card__body">
                        <div class="facility-card__row">
                          <span>Facility Name</span>
                          <p>{{ facility.facility_name || `Facility ${index + 1}` }}</p>
                        </div>
                        <div class="facility-card__row">
                          <span>Count</span>
                          <p>{{ facility.facility_number || '0' }}</p>
                        </div>
                      </div>
                    </article>
                  </div>

                  <p v-else class="empty-copy">No physical facilities available.</p>
                </div>
              </div>
            </section>

            <section id="section-files" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Files</p>
                <h2>Files and documentation</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item">
                  <label>Umbrella Org File</label>
                  <div v-if="organization.umbrella_org_file">
                    <a :href="organization.umbrella_org_file" target="_blank">View resource</a>
                  </div>
                  <div v-else>None</div>
                </div>
                <div class="detail-item">
                  <label>Contact Person CV File</label>
                  <div v-if="organization.contact_person_cv_file">
                    <a :href="organization.contact_person_cv_file" target="_blank">View resource</a>
                  </div>
                  <div v-else>None</div>
                </div>
                <div class="detail-item">
                  <label>Activities Documentation</label>
                  <div v-if="organization.activities_documentation_file">
                    <a :href="organization.activities_documentation_file" target="_blank"
                      >View resource</a
                    >
                  </div>
                  <div v-else>None</div>
                </div>
                <div class="detail-item">
                  <label>Letter of Introduction</label>
                  <div v-if="organization.letter_of_introduction">
                    <a :href="organization.letter_of_introduction" target="_blank">View resource</a>
                  </div>
                  <div v-else>None</div>
                </div>
                <div class="detail-item">
                  <label>File Logo</label>
                  <div v-if="organization.file_logo">
                    <a :href="organization.file_logo" target="_blank">View resource</a>
                  </div>
                  <div v-else>None</div>
                </div>
              </div>
            </section>

            <section id="section-meta" class="detail-section">
              <div class="detail-section__header">
                <p class="section-kicker">Meta</p>
                <h2>Meta information</h2>
              </div>
              <div class="detail-grid-inner">
                <div class="detail-item">
                  <label>Registered On</label>
                  <div>{{ formatDisplayDate(organization.registered_on) }}</div>
                </div>
                <div class="detail-item">
                  <label>Last Updated</label>
                  <div>{{ formatDisplayDate(organization.updated_on) }}</div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div v-else class="loading-shell">
        <p>Unable to load organization details.</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getOrganization,
  approveOrganization,
  rejectOrganization,
} from '../../config/api-modules/api.organization';

export default {
  data() {
    return {
      organization: null,
      isLoading: false,
      testDocs: 'https://s24.q4cdn.com/216390268/files/doc_downloads/test.pdf',
      isBusy: false,
      message: null,
      showReasonBox: false,
      showApproveBox: false,
      reason: '',
      from: null,
      contact: [],
    };
  },
  computed: {
    sectionLinks() {
      return [
        { id: 'general', label: 'General' },
        { id: 'contact', label: 'Contacts' },
        { id: 'basic', label: 'Basic' },
        { id: 'funding', label: 'Funding' },
        { id: 'extra', label: 'Additional' },
        { id: 'resources', label: 'Resources' },
        { id: 'files', label: 'Files' },
        { id: 'meta', label: 'Meta' },
      ];
    },
  },
  methods: {
    getListValues(value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      let normalizedValue = value;

      if (typeof normalizedValue === 'string') {
        const trimmedValue = normalizedValue.trim();

        if (!trimmedValue) {
          return [];
        }

        try {
          normalizedValue = JSON.parse(trimmedValue);
        } catch (error) {
          return [trimmedValue];
        }
      }

      if (Array.isArray(normalizedValue)) {
        return normalizedValue.map((item) => String(item || '').trim()).filter(Boolean);
      }

      return [String(normalizedValue).trim()].filter(Boolean);
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 'Not Provided';
      }

      const normalizedValue = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
      const amount = Number(normalizedValue);

      if (Number.isNaN(amount)) return value;

      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(amount);
    },
    formatDisplayDate(value) {
      if (!value) return 'Not Provided';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Not Provided';

      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    async getOrganizationByID() {
      this.isLoading = true;
      const { id } = this.$route.params;

      try {
        const { data } = await getOrganization(id);
        const payload = data?.data || data || null;
        this.organization = payload;
        this.contact = payload?.contact_persons || payload?.contact || [];
      } catch {
        this.organization = null;
        this.contact = [];
      } finally {
        this.isLoading = false;
      }
    },
    getUserEmail(organization) {
      return organization?.user?.email || 'Not Provided';
    },
    getContactName(person = {}) {
      return (
        person.full_name ||
        [person.first_name, person.last_name].filter(Boolean).join(' ') ||
        'Contact person'
      );
    },
    contactInitials(person) {
      return this.getContactName(person)
        .split(/\s+/)
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();
    },
    getContactPhone(person = {}) {
      return person.phone_number || person.phone || 'Not Provided';
    },

    async approve() {
      this.showReasonBox = false;
      this.showApproveBox = true;
    },

    async confirmApprove() {
      const { id } = this.$route.params;

      try {
        await approveOrganization(id);

        this.$swal({
          title: 'Success',
          text: 'Organization Approved Successfully',
          icon: 'success',
        });

        this.$router.push('/ngo/registered');
      } catch {
        this.$swal({
          title: 'Error',
          text: 'Error Approving Organization',
          icon: 'error',
        });
      } finally {
        this.showApproveBox = false;
        this.isLoading = false;
      }
    },
    cancelApprove() {
      this.showApproveBox = false;
    },
    async reject() {
      if (this.showReasonBox === false) {
        this.showApproveBox = false;
        this.showReasonBox = true;
      } else {
        const { id } = this.$route.params;

        console.log(id);

        const payload = {
          id,
          body: {
            reason: this.reason,
          },
        };

        try {
          await rejectOrganization(payload);

          this.showReasonBox = false;
          this.reason = '';
          this.$swal({
            title: 'Success',
            text: 'Organization Rejected Successfully',
            icon: 'success',
          });

          this.$router.push('/ngo/registered');
        } catch {
          this.$swal({
            title: 'Error',
            text: 'Error Rejecting Organization',
            icon: 'error',
          });
        } finally {
          this.isLoading = false;
        }
      }
    },
    cancelReject() {
      this.showReasonBox = false;
      this.reason = '';
    },
  },
  mounted() {
    this.from = localStorage.getItem('from');
    this.getOrganizationByID();
  },
};
</script>

<style lang="scss" scoped>
.registered-details-page {
  min-height: 100vh;
  padding: 1rem 0 2.5rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.14), transparent 24%),
    linear-gradient(180deg, #f5faf6 0%, #eef5ef 100%);
}

.message-wrapper {
  max-width: 1360px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: auto;
  max-width: max-content;
  align-self: flex-start;
  padding: 0.85rem 1.15rem;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(18, 43, 25, 0.06);
  color: #1f3826;
  font-weight: 700;
  line-height: 1;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.back-button:hover {
  transform: translateY(-1px);
  background: #ffffff;
  box-shadow: 0 14px 28px rgba(18, 43, 25, 0.1);
}

.back-button i {
  font-size: 0.9rem;
}

.details-shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.details-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 28px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.hero-copy {
  max-width: 48rem;
}

.hero-eyebrow,
.section-kicker {
  margin-bottom: 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-eyebrow {
  color: rgba(247, 255, 247, 0.72);
}

.details-hero h1 {
  margin-bottom: 0.85rem;
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.08;
}

.hero-description {
  margin-bottom: 1.2rem;
  max-width: 42rem;
  color: rgba(247, 255, 247, 0.82);
  line-height: 1.75;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hero-pill {
  display: inline-flex;
  align-items: center;
  min-height: 2.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
}

.hero-pill--primary {
  background: rgba(232, 245, 235, 0.18);
  color: #d9ffe1;
  font-weight: 700;
}

.hero-pill--muted {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(247, 255, 247, 0.82);
}

.hero-stats {
  display: grid;
  gap: 1rem;
  min-width: 16rem;
}

.hero-stat {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.hero-stat__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.hero-stat__value {
  font-size: 1.2rem;
}

.section-nav {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.2rem 1.35rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 40px rgba(22, 50, 28, 0.06);
}

.section-kicker {
  color: #2c8d41;
}

.section-nav__copy h3 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-size: 1.15rem;
  color: #14261a;
}

.section-nav__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.6rem;
}

.section-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2.4rem;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.84rem;
  font-weight: 700;
  text-decoration: none;
}

.details-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;
}

.details-main {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.detail-section,
.review-card {
  padding: 1.4rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
  box-shadow: 0 10px 28px rgba(18, 42, 24, 0.05);
  scroll-margin-top: 24px;
}

.detail-section__header {
  margin-bottom: 1rem;
}

.detail-section__header h2,
.review-card h2 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  color: #14261a;
}

.detail-grid-inner {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.detail-item--wide {
  grid-column: 1 / -1;
}

.detail-item--plain > div {
  padding: 0;
  border: 0;
  background: transparent;
}

.detail-item--no-shell {
  gap: 0.7rem;
}

.detail-item--no-shell > div,
.detail-item--no-shell .plain-surface,
.detail-item--no-shell .banker-grid {
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.plain-surface {
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
}

.banker-surface {
  margin-top: 0.1rem;
}

.project-surface {
  margin-top: 0.1rem;
}

.detail-item label,
.contact-card__item span {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.detail-item > div {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  color: #213228;
  line-height: 1.65;
  word-break: break-word;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.contact-card {
  padding: 1.15rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 22px;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.08), transparent 32%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(244, 248, 245, 0.9));
  box-shadow: 0 14px 34px rgba(18, 42, 24, 0.06);
}

.contact-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.contact-card__identity {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.contact-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 12px 24px rgba(48, 171, 76, 0.2);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.contact-card__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6d7d74;
}

.contact-card h3 {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
  color: #1c3022;
}

.contact-card__status {
  display: inline-flex;
  align-items: center;
  padding: 0.42rem 0.8rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.76rem;
  font-weight: 700;
}

.contact-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.contact-card__item {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(28, 68, 38, 0.06);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.74);
}

.contact-card__item--wide {
  grid-column: 1 / -1;
}

.contact-card__item p {
  margin: 0;
  margin-top: 0.3rem;
  color: #213228;
  overflow-wrap: anywhere;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.tags {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-weight: 600;
}

.resource-layout {
  display: grid;
  gap: 1rem;
}

.resource-block {
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.78);
}

.resource-block__header {
  margin-bottom: 1rem;
}

.resource-block__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6d7d74;
}

.resource-block h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1c3022;
}

.resource-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.project-grid {
  display: grid;
  gap: 0.85rem;
}

.banker-grid {
  display: grid;
  gap: 0.85rem;
}

.banker-card {
  display: block;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(28, 68, 38, 0.06);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.68);
}

.banker-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.banker-card__item span {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.banker-card__item p {
  margin: 0;
  color: #213228;
}

.project-card {
  padding: 1.15rem 1.2rem;
  border: 1px solid rgba(28, 68, 38, 0.06);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.project-card__header {
  margin-bottom: 1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
}

.project-card__header h4 {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1c3022;
}

.project-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.5rem;
}

.project-card__row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.project-card__row span {
  display: block;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.project-card__row p {
  margin: 0;
  color: #213228;
}

.resource-card,
.facility-card {
  padding: 1.1rem 1.15rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.85);
}

.resource-card__header {
  margin-bottom: 1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
}

.resource-card__header h4 {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1c3022;
}

.resource-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}

.resource-card__row,
.facility-card__row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0;
  border: 0;
  background: transparent;
}

.resource-card__row span,
.facility-card__row span {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.resource-card__row p,
.facility-card__row p {
  margin: 0;
  color: #213228;
  overflow-wrap: anywhere;
}

.facility-list {
  display: grid;
  gap: 0.85rem;
}

.facility-card {
  display: block;
}

.facility-card__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}

.review-panel {
  position: sticky;
  top: 1rem;
}

.review-copy {
  margin-bottom: 1rem;
  color: #5d6c63;
  line-height: 1.7;
}

.approved {
  border-radius: 18px;
  color: white;
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 700;
}

.approved--yes {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
}

.approved--no {
  background: linear-gradient(135deg, #8c2f2f 0%, #dc3545 100%);
}

.approved--pending {
  background: linear-gradient(135deg, #7a651c 0%, #d4a017 100%);
}

.reason-text {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid rgba(220, 53, 69, 0.12);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 244, 244, 0.92), rgba(255, 250, 250, 0.92));
}

.reason-text__copy {
  margin: 0 0 0.75rem;
  color: #8a5555;
  line-height: 1.55;
}

.reason-text--approve {
  border-color: rgba(48, 171, 76, 0.14);
  background: linear-gradient(180deg, rgba(240, 249, 242, 0.94), rgba(255, 255, 255, 0.94));
}

.reason-text--approve .reason-text__copy {
  color: #4d7557;
}

.reason-text textarea {
  height: 100px;
  border-radius: 16px;
  padding: 10px;
  border: 1px solid rgba(28, 68, 38, 0.12);
  background: #fff;
}

.action-stack {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.action-button {
  width: 100%;
  min-height: 3.2rem;
  border: 0;
  border-radius: 16px;
  font-weight: 700;
}

.action-button--approve {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
  color: #fff;
}

.action-button--reject {
  background: #fff0f0;
  color: #bb4e4e;
}

.action-button--reject-confirm {
  background: linear-gradient(135deg, #8c2f2f 0%, #dc3545 100%);
  box-shadow: 0 16px 30px rgba(220, 53, 69, 0.2);
  color: #fff;
}

.action-button--cancel {
  margin-top: 0.85rem;
  background: #eef2ef;
  color: #30443a;
}

.empty-copy {
  margin: 0;
  color: #5f6f66;
}

.loading-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  text-align: center;
}

.loading-shell p {
  margin-top: 1rem;
  color: #5f6f66;
}

.load-icon {
  width: 4rem;
  height: 4rem;
  color: #2da047;
}

@media (max-width: 991px) {
  .details-hero,
  .section-nav,
  .details-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .details-hero {
    align-items: flex-start;
  }

  .hero-stats {
    width: 100%;
    min-width: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .section-nav__chips {
    justify-content: flex-start;
  }

  .review-panel {
    position: static;
  }
}

@media (max-width: 767px) {
  .registered-details-page {
    padding: 0.5rem 0 2rem;
  }

  .details-hero,
  .detail-section,
  .review-card,
  .section-nav {
    padding: 1rem;
    border-radius: 22px;
  }

  .detail-grid-inner,
  .contact-grid,
  .resource-grid,
  .project-card__body,
  .resource-card__body,
  .facility-card__body,
  .contact-card__body,
  .hero-stats {
    grid-template-columns: 1fr;
  }

  .contact-card__header,
  .contact-card__identity {
    flex-direction: column;
    align-items: flex-start;
  }

  .facility-card,
  .banker-card {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
